import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { PHONE, PHONE_TEL, EMAIL, WHATSAPP, ADDRESS } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { v: "driving_school", l: "Doorstep Driving School" },
  { v: "self_drive", l: "Car Rental & Tours" },
  { v: "wedding", l: "Weddings & Events" },
  { v: "corporate_fleet", l: "Corporate & Hotels" },
  { v: "general", l: "Something else" },
];

const DURATION_OPTIONS = [
  { v: "hourly", l: "Hourly" },
  { v: "daily", l: "Daily" },
  { v: "monthly", l: "Monthly" },
  { v: "one_time", l: "One-Time" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "self_drive",
    duration: "hourly",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        service: form.service,
        duration: form.duration,
        message: form.message.trim() || undefined,
      };
      await axios.post(`${API}/inquiries`, payload);
      setSent(true);
      toast.success("Thank you. We'll reach out shortly.");
      setForm({
        name: "",
        phone: "",
        email: "",
        service: "self_drive",
        duration: "hourly",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try WhatsApp for instant booking.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 lg:py-36"
      data-testid="contact-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left column */}
          <div className="lg:col-span-5">
            <div className="overline mb-6">Ready to ride?</div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl sm:text-5xl lg:text-[4.6rem] leading-[1.02] tracking-tighter text-[#222]"
            >
              Tell us where —{" "}
              <span className="italic text-[#0F766E]">we&rsquo;ll bring the wheels.</span>
            </motion.h2>
            <p className="mt-6 font-body text-[#4a4a4a] leading-relaxed font-light max-w-md">
              Send your requirement by form or WhatsApp. Share your date, duration, preferred car or model and any special requirement so the PAAT team can respond with availability and a final quote.
            </p>

            <div className="mt-10 space-y-6">
              <a
                href={`tel:${PHONE_TEL}`}
                className="flex items-start gap-4 group"
                data-testid="contact-phone"
              >
                <span className="w-10 h-10 rounded-full border border-[rgba(34,34,34,0.15)] flex items-center justify-center transition-colors group-hover:bg-[#0F766E] group-hover:border-[#0F766E] group-hover:text-[#FDFBF7]">
                  <Phone size={16} strokeWidth={1.7} />
                </span>
                <div>
                  <div className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                    Phone
                  </div>
                  <div className="font-display text-2xl text-[#222]">
                    {PHONE}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${EMAIL}`}
                className="flex items-start gap-4 group"
                data-testid="contact-email"
              >
                <span className="w-10 h-10 rounded-full border border-[rgba(34,34,34,0.15)] flex items-center justify-center transition-colors group-hover:bg-[#0F766E] group-hover:border-[#0F766E] group-hover:text-[#FDFBF7]">
                  <Mail size={16} strokeWidth={1.7} />
                </span>
                <div>
                  <div className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                    Email
                  </div>
                  <div className="font-display text-2xl text-[#222] break-all">
                    {EMAIL}
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-full border border-[rgba(34,34,34,0.15)] flex items-center justify-center">
                  <MapPin size={16} strokeWidth={1.7} />
                </span>
                <div>
                  <div className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                    Studio
                  </div>
                  <div className="font-display text-2xl text-[#222]">
                    {ADDRESS}
                  </div>
                </div>
              </div>
            </div>

            <a
              href={WHATSAPP}
              target="_blank"
              rel="noreferrer"
              className="btn-emerald mt-10"
              data-testid="contact-whatsapp-cta"
            >
              Instant WhatsApp Booking
              <ArrowUpRight size={18} strokeWidth={1.6} />
            </a>
          </div>

          {/* Form column */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 bg-white border border-[rgba(34,34,34,0.08)] rounded-[1.4rem] p-8 lg:p-12 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.15)]"
            data-testid="inquiry-form"
          >
            <div className="text-[0.7rem] uppercase tracking-[0.28em] font-semibold text-[#0F766E] mb-2">
              Booking Enquiry · PAAT
            </div>
            <h3 className="font-display text-3xl lg:text-4xl tracking-tight text-[#222] mb-8">
              Send a request
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="block">
                <span className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                  Full name
                </span>
                <input
                  className="editorial-input"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g., Your Name"
                  required
                  data-testid="form-name-input"
                />
              </label>
              <label className="block">
                <span className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                  Phone
                </span>
                <input
                  className="editorial-input"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+91 ..."
                  required
                  data-testid="form-phone-input"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                  Email <span className="opacity-50">(optional)</span>
                </span>
                <input
                  className="editorial-input"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@domain.com"
                  data-testid="form-email-input"
                />
              </label>

              <label className="block">
                <span className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                  Service
                </span>
                <select
                  className="editorial-input bg-transparent"
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  data-testid="form-service-select"
                >
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                  Duration
                </span>
                <select
                  className="editorial-input bg-transparent"
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                  data-testid="form-duration-select"
                >
                  {DURATION_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.l}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block md:col-span-2">
                <span className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[#4a4a4a]">
                  Message
                </span>
                <textarea
                  className="editorial-input resize-none"
                  name="message"
                  rows={3}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Anything specific we should know?"
                  data-testid="form-message-input"
                />
              </label>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-[0.72rem] text-[#4a4a4a] max-w-sm font-light">
                By submitting you agree to be contacted regarding your inquiry.
                We never share your details.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="btn-emerald disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="form-submit-btn"
              >
                {submitting ? "Sending..." : sent ? "Sent — Send another" : "Send Enquiry"}
                <ArrowUpRight size={16} strokeWidth={1.6} />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
